<template>
  <section
    v-if="variants.v1 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div
      class="tw-grid tw-w-full tw-grid-cols-12 tw-grid-rows-1 tw-items-center tw-bg-white tw-p-5 lg:tw-gap-x-20 2xl:tw-gap-x-28"
      :style="{ color: vars.textColor || 'inherit' }"
      style="border-radius: var(--rounded)"
    >
      <LibraryPopupsVideoPopup v-if="vars.photoImage" :videoUrl="vars.videoUrl" v-model:is-visible="isVisible" />

      <div
        :class="[
          'tw-relative tw-col-span-full lg:tw-col-span-6 xl:tw-col-span-5',
          vars.isReversedBool ? 'lg:tw-order-1' : 'lg:tw-order-2',
        ]"
      >
        <div
          v-if="vars.photoImage && vars.videoUrl && vars.isPopupBool"
          class="tw-relative tw-mb-6 tw-h-full tw-w-full tw-overflow-hidden tw-rounded-[var(--rounded)] lg:tw-mb-0"
          @click="isVisible = true"
        >
          <LibraryImage
            :src="vars.photoImage"
            :alt="vars.titleText"
            image-class="tw-h-full tw-w-full tw-cursor-pointer tw-object-cover"
            :w-ratio="vars.isVerticalBool ? 9 : 3"
            :h-ratio="vars.isVerticalBool ? 16 : 2"
          />
          <button class="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2">
            <svg width="80" height="80" viewBox="0 0 162 162" xmlns="http://www.w3.org/2000/svg">
              <circle cx="81" cy="81" r="81" fill="white" />
              <polygon points="65,45 115,81 65,117" fill="black" />
            </svg>
          </button>
        </div>

        <div
          v-if="vars.photoImage && !vars.videoUrl"
          class="tw-relative tw-mb-6 tw-h-full tw-w-full tw-overflow-hidden tw-rounded-[var(--rounded)] lg:tw-mb-0"
        >
          <LibraryImage
            :src="vars.photoImage"
            :alt="vars.titleText"
            image-class="tw-h-full tw-w-full tw-object-cover"
            :w-ratio="vars.isVerticalBool ? 9 : 645"
            :h-ratio="vars.isVerticalBool ? 16 : 430"
          />
        </div>

        <iframe
          v-if="!vars.isPopupBool && vars.videoUrl"
          class="tw-mb-6 tw-h-full tw-w-full tw-rounded-[var(--rounded)] tw-object-cover lg:tw-mb-0"
          :src="vars.videoUrl"
          :style="vars.isVerticalBool ? 'aspect-ratio:5/8;' : 'aspect-ratio: 16/9;'"
          frameborder="0"
          allowfullscreen
        ></iframe>
        <div
          v-if="vars.videoUrl && vars.isPopupBool"
          class="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2"
        >
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M32 4C26.4621 4 21.0486 5.64217 16.444 8.71885C11.8395 11.7955 8.25064 16.1685 6.13139 21.2849C4.01213 26.4012 3.45764 32.0311 4.53802 37.4625C5.61841 42.894 8.28515 47.8831 12.201 51.799C16.1169 55.7149 21.106 58.3816 26.5375 59.462C31.969 60.5424 37.5988 59.9879 42.7151 57.8686C47.8315 55.7494 52.2045 52.1605 55.2812 47.556C58.3578 42.9514 60 37.5379 60 32C60 24.5739 57.05 17.452 51.799 12.201C46.548 6.94999 39.4261 4 32 4ZM46.894 33.79L22.894 45.79C22.589 45.9424 22.2501 46.0143 21.9095 45.9989C21.5689 45.9835 21.2379 45.8812 20.948 45.7019C20.658 45.5225 20.4187 45.272 20.2528 44.9741C20.0869 44.6763 19.9999 44.341 20 44V20C20.0002 19.6592 20.0875 19.3241 20.2535 19.0265C20.4196 18.729 20.6589 18.4787 20.9488 18.2996C21.2387 18.1205 21.5696 18.0184 21.91 18.0031C22.2505 17.9878 22.5892 18.0597 22.894 18.212L46.894 30.212C47.2258 30.3783 47.5047 30.6336 47.6997 30.9494C47.8946 31.2651 47.9979 31.6289 47.9979 32C47.9979 32.3711 47.8946 32.7349 47.6997 33.0507C47.5047 33.3664 47.2258 33.6217 46.894 33.788"
              fill="white"
            />
          </svg>
        </div>
      </div>

      <MotionGroup
        preset="slideVisibleLeft"
        is="div"
        :duration="800"
        :class="[
          'tw-order-2 tw-col-span-full lg:tw-col-span-6 xl:tw-col-span-7',
          vars.isReversedBool ? 'lg:tw-order-2' : 'lg:tw-order-1 lg:tw-pl-12',
        ]"
      >
        <div v-html="vars.titleHtml" class="tw-mb-6 tw-flex tw-flex-wrap tw-gap-2"></div>
        <div class="tw-flex tw-flex-col tw-gap-3">
          <p
            v-for="(description, index) in descriptions"
            :key="`contentbox-description-${index}`"
            class="tw-text-left"
            :style="`color: var(--c-${vars.textColor})`"
          >
            {{ description.description }}
          </p>
        </div>
        <div class="tw-mt-8 tw-flex tw-flex-wrap tw-gap-3 lg:tw-flex-nowrap">
          <nuxt-link
            v-for="(button, index) in buttons"
            :key="`contentbox-button-${index}`"
            :external="true"
            :to="button.link"
            :class="`n-${button.type} tw-w-full md:tw-w-auto`"
          >
            {{ button.label }}
          </nuxt-link>
        </div>
      </MotionGroup>
    </div>
  </section>

  <div
    v-if="variants.v2 === variant && sidebarComponents.length > 0"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div
      class="tw-grid tw-w-full tw-grid-cols-12 tw-grid-rows-1 tw-items-center tw-bg-white tw-p-5"
      :style="{ color: vars.textColor || 'inherit' }"
      style="border-radius: var(--rounded)"
    >
      <MotionGroup preset="fadeVisible" is="div" :duration="800" :class="['tw-order-2 tw-col-span-full']">
        <div v-html="vars.titleHtml" class="tw-mb-4 tw-flex tw-flex-wrap tw-gap-2"></div>
        <div class="tw-flex tw-flex-col tw-gap-3">
          <p
            v-for="(description, index) in descriptions"
            :key="`contentbox-description-${index}`"
            class="tw-text-left"
            :style="`color: var(--c-${vars.textColor})`"
          >
            {{ description.description }}
          </p>
        </div>
        <div class="tw-mt-8 tw-flex tw-flex-wrap tw-gap-3">
          <nuxt-link
            v-for="(button, index) in buttons"
            :key="`contentbox-button-${index}`"
            :external="true"
            :to="button.link"
            :class="`n-${button.type} tw-w-full md:tw-w-auto`"
          >
            {{ button.label }}
          </nuxt-link>
        </div>
      </MotionGroup>
    </div>
  </div>

  <div
    v-if="variants.v3 === variant && sidebarComponents.length > 0"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div
      class="tw-grid tw-w-full tw-grid-cols-12 tw-grid-rows-1 tw-items-center tw-bg-white tw-px-7 tw-py-6"
      :style="{ color: vars.textColor || 'inherit' }"
      style="border-radius: var(--rounded)"
    >
      <MotionGroup preset="fadeVisibleOnce" is="div" :duration="800" :class="['tw-order-2 tw-col-span-full']">
        <h3 class="n-title-primary tw-mb-4">{{ vars.titleText }}</h3>
        <div class="tw-flex tw-flex-col tw-gap-3">
          <p
            v-for="(description, index) in descriptions"
            :key="`contentbox-description-${index}`"
            class="tw-text-left"
            :style="`color: var(--c-${vars.textColor})`"
          >
            {{ description.description }}
          </p>
        </div>
        <div class="tw-mt-8 tw-flex tw-flex-wrap tw-gap-3">
          <nuxt-link
            v-for="(button, index) in buttons"
            :key="`contentbox-button-${index}`"
            :external="true"
            :to="button.link"
            :class="`n-${button.type} tw-w-full md:tw-w-auto`"
          >
            {{ button.label }}
          </nuxt-link>
        </div>
      </MotionGroup>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';
import { mapState } from 'pinia';
import { useWebsiteStore } from '~/units/website/store';

export default defineNuxtComponent({
  name: 'SmallBox',

  mixins: [ComponentMixin],

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
      isVisible: false,
      plainText: '',
    };
  },

  computed: {
    descriptions() {
      return this.groupedVariables.descriptions;
    },
    buttons() {
      return this.groupedVariables.buttons;
    },

    ...mapState(useWebsiteStore, ['sidebarComponents']),
  },
});
</script>
